import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Copy, Loader2, Plus, RefreshCw, X } from 'lucide-react';

import { KnowledgeParticleQaSections } from '../../components/KnowledgeParticleQaSections';
import { ProxyImage } from '../../components/assets/ProxyImage';
import { MathTextBlock } from '../../components/MathTextBlock';
import {
  kpWorkspaceService,
  type WorkspaceGraphBundle,
  type WorkspaceGraphNode,
} from '../../services/kpWorkspaceService';
import { assetService } from '../../services/assetService';
import type { Asset, KnowledgeParticle } from '../../types/domain';
import { formatDateTime, resolveSubjectBadge } from '../../utils/assetMedia';
import { buildAssetPreviewUrl } from '../../utils/mediaUrl';
import { safeExtractText } from '../../utils/textHelper';
import {
  buildWorkspaceGraphLayout,
  resolveGraphRootNode,
  type GraphLayoutNodeMeta,
} from './workspaceGraphLayout';
import {
  kindLabel,
  resolveParticlePapStripEligible,
  workspaceGraphNodeTypes,
  type WorkspaceGraphNodeData,
} from './WorkspaceGraphNodeCards';

function readNodeText(value: unknown): string {
  return safeExtractText(value);
}

function toFlowNodes(
  layoutNodes: GraphLayoutNodeMeta[],
  selectedId: string | null,
  particlesById: Record<string, KnowledgeParticle>,
  previewUrl: string | null,
  assetOssId: string | null | undefined,
  fallbackSubject: string | null,
  onParticleUpdated: (particle: KnowledgeParticle) => void,
  onStubInteract: () => void,
): Node<WorkspaceGraphNodeData>[] {
  return layoutNodes.map((item) => {
    const particleId = item.node.particleId || item.node.id.replace(/^particle:/, '');
    const type = item.node.kind === 'asset'
      ? 'workspaceAsset'
      : item.node.kind === 'particle'
        ? 'workspaceParticle'
        : 'workspaceDerived';
    return {
      id: item.node.id,
      type,
      position: { x: item.x, y: item.y },
      style: { width: item.width, height: item.height },
      data: {
        node: item.node,
        selected: selectedId === item.node.id,
        particle: particleId ? particlesById[particleId] || null : null,
        previewUrl: item.node.kind === 'asset' ? previewUrl : null,
        assetOssId,
        fallbackSubject,
        onParticleUpdated,
        onStubInteract,
      },
      draggable: false,
      selectable: true,
    };
  });
}

function toFlowEdges(layoutEdges: WorkspaceGraphBundle['edges']): Edge[] {
  return layoutEdges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    type: 'smoothstep',
    animated: edge.relation === 'particle_derived',
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 16,
      height: 16,
      color: edge.relation === 'asset_particle' ? '#D97B7B' : '#0F6CBD',
    },
    style: {
      stroke: edge.relation === 'asset_particle' ? '#D97B7B' : '#0F6CBD',
      strokeWidth: 2,
    },
  }));
}

type WorkspaceGraphViewProps = {
  assetId?: string;
  particleId?: string;
  className?: string;
};

export function WorkspaceGraphView({ assetId, particleId, className = '' }: WorkspaceGraphViewProps) {
  const [bundle, setBundle] = useState<WorkspaceGraphBundle | null>(null);
  const [particlesById, setParticlesById] = useState<Record<string, KnowledgeParticle>>({});
  const [assetRecord, setAssetRecord] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<WorkspaceGraphNode | null>(null);
  const [inspectorVisible, setInspectorVisible] = useState(false);
  const [derivePrompt, setDerivePrompt] = useState('');
  const [deriving, setDeriving] = useState(false);
  const [copiedAssetId, setCopiedAssetId] = useState(false);
  const ignoreStubNodeClickRef = useRef(false);

  const handleParticleUpdated = useCallback((updated: KnowledgeParticle) => {
    setParticlesById((prev) => ({ ...prev, [updated.id]: updated }));
  }, []);

  const handleStubInteract = useCallback(() => {
    ignoreStubNodeClickRef.current = true;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = assetId
        ? await kpWorkspaceService.getAssetWorkspaceGraph(assetId)
        : particleId
          ? await kpWorkspaceService.getParticleWorkspaceGraph(particleId)
          : null;
      if (!next) throw new Error('缺少 assetId 或 particleId');
      setBundle(next);

      if (assetId) {
        const [particles, asset] = await Promise.all([
          assetService.listKnowledgeParticlesByAsset(assetId).catch(() => [] as KnowledgeParticle[]),
          assetService.getAsset(assetId).catch(() => null),
        ]);
        const nextParticlesById: Record<string, KnowledgeParticle> = {};
        for (const particle of particles) nextParticlesById[particle.id] = particle;
        setParticlesById(nextParticlesById);
        setAssetRecord(asset);
      } else {
        setParticlesById({});
        setAssetRecord(null);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载关系图失败');
    } finally {
      setLoading(false);
    }
  }, [assetId, particleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rootNode = useMemo(
    () => (bundle ? resolveGraphRootNode(bundle, assetId, particleId) : null),
    [assetId, bundle, particleId],
  );

  const previewUrl = useMemo(() => {
    const raw = bundle?.root.surface?.previewUrl || assetRecord?.url || '';
    return buildAssetPreviewUrl(raw) || raw || null;
  }, [assetRecord?.url, bundle?.root.surface?.previewUrl]);

  const fallbackSubject = assetRecord?.subject
    || (assetRecord?.ai_category === '数学' ? 'math' : assetRecord?.ai_category)
    || null;

  const papStripNodeIds = useMemo(() => {
    const ids = new Set<string>();
    if (!bundle) return ids;
    for (const node of bundle.nodes) {
      const pid = node.particleId || node.id.replace(/^particle:/, '');
      if (resolveParticlePapStripEligible(node, particlesById[pid] || null, fallbackSubject)) {
        ids.add(node.id);
      }
    }
    return ids;
  }, [bundle, fallbackSubject, particlesById]);

  const { layoutNodes, layoutEdges } = useMemo(
    () => (bundle && rootNode
      ? buildWorkspaceGraphLayout(bundle, rootNode, { papStripNodeIds })
      : { layoutNodes: [], layoutEdges: [] }),
    [bundle, papStripNodeIds, rootNode],
  );

  const selectedId = inspectorVisible ? selected?.id || null : null;
  const flowNodes = useMemo(
    () => toFlowNodes(
      layoutNodes,
      selectedId,
      particlesById,
      previewUrl,
      assetRecord?.oss_id,
      fallbackSubject,
      handleParticleUpdated,
      handleStubInteract,
    ),
    [assetRecord?.oss_id, fallbackSubject, handleParticleUpdated, handleStubInteract, layoutNodes, particlesById, previewUrl, selectedId],
  );
  const flowEdges = useMemo(() => toFlowEdges(layoutEdges), [layoutEdges]);

  const selectedParticleId = selected?.particleId
    || (selected?.kind === 'particle' || selected?.kind === 'derived'
      ? selected.id.replace(/^particle:/, '')
      : '');

  async function handleDerive() {
    if (!selected?.particleId || !derivePrompt.trim()) return;
    setDeriving(true);
    setError(null);
    try {
      await kpWorkspaceService.submitWorkspaceTurn({
        focusParticleId: selected.particleId,
        particleTitle: selected.title,
        content: derivePrompt.trim(),
        graphNodeMode: true,
        parentArtifactId: selected.artifactId,
        suggestedActions: ['KpDrillOnPurpose'],
      });
      setDerivePrompt('');
      await load();
    } catch (deriveError) {
      setError(deriveError instanceof Error ? deriveError.message : '衍生失败');
    } finally {
      setDeriving(false);
    }
  }

  async function handleCopyAssetId(id: string) {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedAssetId(true);
      window.setTimeout(() => setCopiedAssetId(false), 1600);
    } catch {
      setCopiedAssetId(false);
    }
  }

  if (loading) {
    return (
      <div className={`flex h-[calc(100dvh-15rem)] min-h-[480px] items-center justify-center rounded-2xl border border-border bg-[#EDF1F7] ${className}`}>
        <div className="inline-flex items-center gap-2 text-sm text-muted">
          <Loader2 className="animate-spin text-primary" size={18} />
          正在加载关系视图…
        </div>
      </div>
    );
  }

  if (error && !bundle) {
    return (
      <div className={`rounded-xl border border-danger/30 bg-danger/5 p-4 text-sm text-danger ${className}`}>
        {error}
        <button type="button" onClick={() => void load()} className="ml-3 underline">重试</button>
      </div>
    );
  }

  const inspectorTitle = readNodeText(selected?.title) || selected?.title || '节点详情';
  const inspectorSubtitle = readNodeText(selected?.subtitle);
  const subjectBadge = assetRecord ? resolveSubjectBadge(assetRecord) : null;
  const assetOpsId = selected?.kind === 'asset' ? (selected.assetId || assetRecord?.id || '') : '';

  return (
    <div className={`relative h-[calc(100dvh-15rem)] min-h-[520px] overflow-hidden rounded-2xl border border-border bg-[#EDF1F7] shadow-card ${className}`}>
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={workspaceGraphNodeTypes}
        fitView
        fitViewOptions={{ padding: 0.18, maxZoom: 1.1 }}
        minZoom={0.35}
        maxZoom={1.4}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        proOptions={{ hideAttribution: true }}
        onNodeClick={(event, node) => {
          if (ignoreStubNodeClickRef.current) {
            ignoreStubNodeClickRef.current = false;
            return;
          }
          const target = event.target as HTMLElement | null;
          if (target?.closest?.('[data-graph-stub]')) {
            return;
          }
          setSelected(node.data.node);
          setInspectorVisible(true);
        }}
        onPaneClick={() => setInspectorVisible(false)}
      >
        <Background gap={18} size={1} color="#CBD5E1" />
        <Controls showInteractive={false} />
        <MiniMap
          pannable
          zoomable
          nodeColor={(node) => {
            if (node.type === 'workspaceAsset') return '#D97B7B';
            if (node.type === 'workspaceParticle') return '#0F6CBD';
            return '#94A3B8';
          }}
        />
      </ReactFlow>

      <button
        type="button"
        onClick={() => void load()}
        className="absolute right-3 top-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface/95 text-muted shadow-card hover:text-primary"
        aria-label="刷新"
      >
        <RefreshCw size={16} />
      </button>

      {inspectorVisible && selected ? (
        <aside className="absolute left-4 top-4 z-20 flex max-h-[calc(100%-2rem)] w-[min(360px,calc(100%-2rem))] flex-col gap-3 overflow-hidden rounded-2xl border border-border bg-white/96 p-4 shadow-[0_18px_28px_rgba(7,17,31,0.22)]">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-extrabold text-primary-strong">{kindLabel(selected.kind)}</p>
              <h3 className="mt-0.5 line-clamp-3 text-sm font-semibold text-ink">{inspectorTitle}</h3>
            </div>
            <button
              type="button"
              onClick={() => setInspectorVisible(false)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ink hover:bg-black/[0.04]"
              aria-label="关闭节点详情"
            >
              <X size={16} />
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            {selected.kind === 'asset' && previewUrl ? (
              <ProxyImage
                rawUrl={previewUrl}
                ossId={assetRecord?.oss_id}
                alt={inspectorTitle}
                className="max-h-48 min-h-[120px] w-full rounded-lg"
                imgClassName="max-h-48 object-cover"
                zoomable
                zoomTitle={inspectorTitle}
              />
            ) : null}

            {selected.kind === 'asset' ? (
              <div className="space-y-2 text-sm">
                {inspectorSubtitle ? <p className="text-muted">{inspectorSubtitle}</p> : null}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted">学科</span>
                  <span className="text-xs text-ink">{subjectBadge?.label || assetRecord?.subject || '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted">上传时间</span>
                  <span className="text-xs text-ink">{assetRecord?.created_at ? formatDateTime(assetRecord.created_at) : '—'}</span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <span className="shrink-0 text-xs text-muted">素材 ID</span>
                  <div className="flex min-w-0 items-center gap-1">
                    <span className="truncate text-xs text-ink" title={assetOpsId}>{assetOpsId || '—'}</span>
                    {assetOpsId ? (
                      <button
                        type="button"
                        onClick={() => void handleCopyAssetId(assetOpsId)}
                        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold text-primary-strong hover:bg-primary/5"
                        aria-label="复制素材 ID"
                      >
                        <Copy size={12} />
                        {copiedAssetId ? '已复制' : '复制'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            {(selected.kind === 'particle' || (selected.kind === 'derived' && selectedParticleId && particlesById[selectedParticleId])) && selectedParticleId ? (
              <KnowledgeParticleQaSections
                particle={particlesById[selectedParticleId] || null}
                fallbackQuestionText={readNodeText(selected.question) || inspectorSubtitle}
              />
            ) : null}

            {selected.kind === 'derived' && !particlesById[selectedParticleId] ? (
              <div className="space-y-2 text-sm text-muted">
                {selected.question ? (
                  <div>
                    <p className="text-xs font-semibold text-ink">问题</p>
                    <MathTextBlock text={readNodeText(selected.question)} />
                  </div>
                ) : null}
                {selected.answer ? (
                  <div>
                    <p className="text-xs font-semibold text-ink">答案</p>
                    <MathTextBlock text={readNodeText(selected.answer)} />
                  </div>
                ) : null}
              </div>
            ) : null}

            {selected.kind !== 'asset' && selected.particleId ? (
              <div className="space-y-2 border-t border-border pt-3">
                <label className="text-sm font-medium text-ink">衍生线索</label>
                <textarea
                  value={derivePrompt}
                  onChange={(e) => setDerivePrompt(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm"
                  placeholder="输入一条新的 KpAction 线索…"
                />
                <button
                  type="button"
                  disabled={deriving || !derivePrompt.trim()}
                  onClick={() => void handleDerive()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50"
                >
                  <Plus size={16} />
                  {deriving ? '提交中…' : '发起衍生'}
                </button>
              </div>
            ) : null}
          </div>
        </aside>
      ) : null}

      {error ? (
        <p className="absolute bottom-3 left-4 right-4 z-20 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
      ) : null}
    </div>
  );
}
